import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PsychologyIcon from '@mui/icons-material/Psychology';
import HubIcon from '@mui/icons-material/Hub';
import GridViewIcon from '@mui/icons-material/GridView';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';

export default function PokerStudyLandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handlePrimaryAction = () => {
    navigate(isAuthenticated ? '/poker-study' : '/poker/login');
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        overflowX: 'hidden',
        bgcolor: '#0f172a',
        color: '#f8fafc',
      }}
    >
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          background:
            'radial-gradient(circle at top left, rgba(56,189,248,0.18), transparent 34%), radial-gradient(circle at bottom right, rgba(252,211,77,0.10), transparent 30%), #0f172a',
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 }, position: 'relative', zIndex: 2 }}>
          <Stack spacing={6}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Stack direction="row" spacing={1.4} alignItems="center">
                <Box
                  component="img"
                  src="/logo.png"
                  alt="PROA"
                  sx={{
                    height: 36,
                    width: 'auto',
                    filter: 'drop-shadow(0 0 12px rgba(56,189,248,0.28))',
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: '#f8fafc',
                      letterSpacing: '-0.4px',
                      lineHeight: 1.1,
                    }}
                  >
                    PokerStudy
                  </Typography>
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                    }}
                  >
                    PROA Study System
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="outlined"
                onClick={() => navigate(isAuthenticated ? '/poker-study' : '/poker/login')}
                sx={{
                  borderRadius: 2,
                  px: 2.4,
                  py: 1,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  color: '#cbd5e1',
                  borderColor: alpha('#94a3b8', 0.35),
                  '&:hover': {
                    borderColor: '#38bdf8',
                    color: '#f8fafc',
                    bgcolor: alpha('#38bdf8', 0.08),
                  },
                }}
              >
                {isAuthenticated ? 'Abrir app' : 'Iniciar sesión'}
              </Button>
            </Stack>

            <Grid container spacing={5} alignItems="center">
              <Grid item xs={12} md={6.4}>
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: '#fcd34d',
                        fontWeight: 800,
                        letterSpacing: 3,
                        fontSize: '0.82rem',
                      }}
                    >
                      SISTEMA DE ESTUDIO DE POKER
                    </Typography>

                    <Typography
                      component="h1"
                      sx={{
                        mt: 1.5,
                        fontWeight: 850,
                        letterSpacing: '-1.6px',
                        lineHeight: 1.05,
                        color: '#f8fafc',
                        fontSize: { xs: '3rem', md: '4.7rem' },
                        maxWidth: 760,
                      }}
                    >
                      Estudia rangos, boards y líneas con estructura profesional.
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontWeight: 300,
                      fontSize: { xs: '1rem', md: '1.15rem' },
                      lineHeight: 1.7,
                      maxWidth: 650,
                      textAlign: 'justify',
                    }}
                  >
                    PokerStudy convierte spots aislados en un sistema de trabajo: define
                    situaciones, crea plantillas de rangos, organiza boards por textura y
                    estudia líneas postflop por nodos sin depender de notas dispersas.
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handlePrimaryAction}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        px: 4,
                        py: 1.3,
                        fontSize: '1rem',
                        fontWeight: 800,
                        bgcolor: '#38bdf8',
                        color: '#0f172a',
                        '&:hover': { bgcolor: '#0ea5e9' },
                      }}
                    >
                      {isAuthenticated ? 'Entrar al sistema' : 'Acceso PokerStudy'}
                    </Button>

                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/poker/login')}
                      sx={{
                        px: 4,
                        py: 1.3,
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: '#f8fafc',
                        borderColor: alpha('#94a3b8', 0.38),
                        '&:hover': {
                          borderColor: '#fcd34d',
                          bgcolor: alpha('#fcd34d', 0.08),
                        },
                      }}
                    >
                      Usuarios de prueba
                    </Button>
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} md={5.6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 4,
                    bgcolor: alpha('#1e293b', 0.74),
                    border: `1px solid ${alpha('#334155', 0.9)}`,
                    boxShadow: '0 0 50px rgba(0, 0, 0, 0.38)',
                    backdropFilter: 'blur(14px)',
                  }}
                >
                  <Stack spacing={1.5}>
                    {[
                      {
                        icon: PsychologyIcon,
                        title: 'Pensamiento estructurado',
                        text: 'Convierte heurísticas, notas y líneas en un sistema de estudio mantenible.',
                        color: '#38bdf8',
                      },
                      {
                        icon: GridViewIcon,
                        title: 'Rangos reutilizables',
                        text: 'Crea plantillas como OR CO o BB defend e impórtalas en spots y boards.',
                        color: '#4ade80',
                      },
                      {
                        icon: HubIcon,
                        title: 'Boards como laboratorio',
                        text: 'Agrupa flops, texturas y líneas para estudiar patrones concretos.',
                        color: '#fcd34d',
                      },
                      {
                        icon: AccountTreeIcon,
                        title: 'Árboles por nodos',
                        text: 'Trabaja decisiones postflop, runouts, acciones y notas por punto de la línea.',
                        color: '#a78bfa',
                      },
                    ].map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          p: 1.7,
                          borderRadius: 3,
                          bgcolor: alpha('#ffffff', 0.035),
                          border: `1px solid ${alpha(item.color, 0.18)}`,
                          transition: 'all 0.25s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            borderColor: alpha(item.color, 0.48),
                            bgcolor: alpha('#ffffff', 0.055),
                          },
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              bgcolor: alpha(item.color, 0.1),
                              color: item.color,
                              display: 'flex',
                              flexShrink: 0,
                            }}
                          >
                            <item.icon sx={{ fontSize: 24 }} />
                          </Box>

                          <Box>
                            <Typography sx={{ fontWeight: 850, color: '#f8fafc' }}>
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.4,
                                color: '#94a3b8',
                                fontSize: 13.5,
                                lineHeight: 1.55,
                              }}
                            >
                              {item.text}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </Container>
      </Box>

      <Divider sx={{ opacity: 0.1 }} />

      <Box sx={{ py: { xs: 8, md: 11 }, bgcolor: alpha('#000', 0.14) }}>
        <Container maxWidth="lg">
          <Stack spacing={5}>
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                variant="overline"
                sx={{
                  color: '#38bdf8',
                  fontWeight: 800,
                  letterSpacing: 2.5,
                }}
              >
                FLUJO DE TRABAJO
              </Typography>

              <Typography
                sx={{
                  mt: 1.5,
                  color: '#f8fafc',
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '2.8rem' },
                  letterSpacing: '-0.8px',
                }}
              >
                De una idea suelta a un spot estudiable.
              </Typography>

              <Typography
                sx={{
                  mt: 1.5,
                  color: '#94a3b8',
                  fontSize: '1.05rem',
                  lineHeight: 1.7,
                  maxWidth: 720,
                }}
              >
                El objetivo no es calcular EV ni sustituir un solver. Es construir una
                base de conocimiento propia para revisar situaciones, comparar líneas y
                mantener criterios de estudio consistentes.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {[
                {
                  title: '1. Define el spot',
                  text: 'SRP, 3Bet pot, posiciones, stack efectivo y perfil del rival.',
                  color: '#38bdf8',
                },
                {
                  title: '2. Carga rangos base',
                  text: 'Importa plantillas preflop y adapta el rango al spot concreto.',
                  color: '#4ade80',
                },
                {
                  title: '3. Estudia boards',
                  text: 'Crea flops, clasifica texturas y analiza cómo cambia la estrategia.',
                  color: '#fcd34d',
                },
                {
                  title: '4. Construye líneas',
                  text: 'Avanza por nodos, acciones, turn, river y notas estratégicas.',
                  color: '#a78bfa',
                },
              ].map((step) => (
                <Grid item xs={12} md={3} key={step.title}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      height: '100%',
                      borderRadius: 4,
                      bgcolor: alpha('#1e293b', 0.42),
                      border: `1px solid ${alpha(step.color, 0.2)}`,
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        borderColor: alpha(step.color, 0.52),
                        boxShadow: `0 18px 40px -20px ${alpha(step.color, 0.45)}`,
                      },
                    }}
                  >
                    <Typography sx={{ color: step.color, fontWeight: 850, mb: 1 }}>
                      {step.title}
                    </Typography>

                    <Typography sx={{ color: '#cbd5e1', fontSize: 14, lineHeight: 1.65 }}>
                      {step.text}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      <Divider sx={{ opacity: 0.1 }} />

      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              bgcolor: '#1e293b',
              border: '1px solid #334155',
              boxShadow: '0 0 50px rgba(0, 0, 0, 0.42)',
              textAlign: 'center',
            }}
          >
            <Stack spacing={2.5} alignItems="center">
              <Chip
                label="Acceso privado en fase de prueba"
                sx={{
                  fontWeight: 850,
                  color: '#fcd34d',
                  bgcolor: alpha('#fcd34d', 0.1),
                  border: `1px solid ${alpha('#fcd34d', 0.28)}`,
                }}
              />

              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 850,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  letterSpacing: '-0.7px',
                  lineHeight: 1.1,
                }}
              >
                Accede a tu entorno de estudio PokerStudy.
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  maxWidth: 620,
                  lineHeight: 1.7,
                }}
              >
                En esta fase el acceso está pensado para usuarios de prueba y desarrollo
                controlado. La experiencia ya queda separada visualmente del resto de PROA.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handlePrimaryAction}
                  sx={{
                    px: 4,
                    py: 1.35,
                    bgcolor: '#38bdf8',
                    color: '#0f172a',
                    fontWeight: 850,
                    '&:hover': { bgcolor: '#0ea5e9' },
                  }}
                >
                  {isAuthenticated ? 'Abrir PokerStudy' : 'Entrar a PokerStudy'}
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/poker/login')}
                  sx={{
                    px: 4,
                    py: 1.35,
                    color: '#cbd5e1',
                    borderColor: alpha('#94a3b8', 0.35),
                    fontWeight: 800,
                    '&:hover': {
                      color: '#f8fafc',
                      borderColor: '#f8fafc',
                      bgcolor: alpha('#fff', 0.03),
                    },
                  }}
                >
                  Ir al login
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#64748b' }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: '#4ade80' }} />
                <Typography variant="caption">
                  Spots · Boards · Rangos · Nodos · Notas
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}